import useQueryWithTracing, { type ExtendedQueryResult } from '../../useQueryWithTracing';
import surveyQuery from './Survey.query.graphql';
import type {
  SurveyFragmentFragment,
  SurveyQuery,
  SurveyQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import type {
  AssociatedImage,
  SurveyProperties
} from '@aurora/shared-generated/types/graphql-schema-types';
import {
  getSurveyQuestionCount,
  removeDisabledQuestionAndAnswers
} from '../../../helpers/surveys/SurveyHelper';

export type FilteredSurveyWithFinalQuestionCountAndProperties = SurveyFragmentFragment &
  Pick<
    SurveyProperties,
    'surveyBrandName' | 'surveyCommunityName' | 'surveyHeaderBgColor' | 'surveyHeaderTextColor'
  > & {
    finalQuestionCount: number;
    surveyBrandingLogo?: AssociatedImage;
  };

/**
 * Hook to fetch survey data
 *
 * @author Adam Ayres
 */
export default function useSurvey(skip: boolean): ExtendedQueryResult<
  SurveyQuery,
  SurveyQueryVariables
> & {
  computedSurvey: FilteredSurveyWithFinalQuestionCountAndProperties;
} {
  const queryResult = useQueryWithTracing<SurveyQuery, SurveyQueryVariables>(module, surveyQuery, {
    skip
  });

  const { data } = queryResult;
  let filteredSurveyWithFinalQuestionCountAndProperties: FilteredSurveyWithFinalQuestionCountAndProperties =
    null;

  if (data) {
    const surveyDisabledInputs = data.community.surveyProperties.surveyDisabledInputs ?? [];
    const { surveyCommunityName, surveyBrandName, surveyHeaderBgColor, surveyHeaderTextColor } =
      data.community.surveyProperties;

    const filteredSurvey: SurveyFragmentFragment = removeDisabledQuestionAndAnswers(
      surveyDisabledInputs,
      data?.valueSurvey
    );

    filteredSurveyWithFinalQuestionCountAndProperties = {
      ...filteredSurvey,
      surveyCommunityName,
      surveyBrandName,
      surveyHeaderBgColor,
      surveyHeaderTextColor,
      surveyBrandingLogo: data.surveyBrandingLogo ?? null,
      finalQuestionCount: getSurveyQuestionCount(filteredSurvey)
    };
  }

  return { ...queryResult, computedSurvey: filteredSurveyWithFinalQuestionCountAndProperties };
}
